import { useState } from "react";
import { AICC_SANDBOX_ACTION } from "constants/aiccPromptSandbox";
import { AiccSandboxFeedbackResult, AiccSandboxTurn, AiccSandboxWordsResult } from "types/model/aiccPromptSandbox";
import * as sandboxAPI from "services/aiccPromptSandbox";
import { AiccSandboxLessonRequest } from "types/dto/request/aiccPromptSandbox";
import { SandboxRequestBase } from "../sandboxRequest";

interface Options {
  languageId?: number;
  requestBase: () => SandboxRequestBase;
  transcript: AiccSandboxTurn[];
  lesson?: AiccSandboxLessonRequest;
  /**
   * Which feedback prompt this panel is testing — see `AICC_SANDBOX_FEEDBACK_VARIANT`.
   *
   * REQUIRED, because the two feedback prompts are separate flows and the backend refuses a call that names
   * neither. Optional, it read as a default: the scenario panel passed nothing and every summary it asked for
   * came back a 400.
   *
   * Taken rather than derived from `lesson`: that coordinate feeds `lesson_information`, which the SCENARIO prompt
   * declares too, so inferring the flow from it would be reading one fact as an answer to a different question.
   */
  feedbackVariant: string;
}

export function useSandboxSupport({ languageId, requestBase, transcript, lesson, feedbackVariant }: Options) {
  const [feedback, setFeedback] = useState<AiccSandboxFeedbackResult | null>();
  const [isSummarising, setIsSummarising] = useState(false);
  const [words, setWords] = useState<AiccSandboxWordsResult | null>();
  const [isIdentifying, setIsIdentifying] = useState(false);

  const summarise = () => {
    if (!languageId) {
      return;
    }

    setIsSummarising(true);
    sandboxAPI
      .runAiccSandboxAction(languageId, AICC_SANDBOX_ACTION.feedback, {
        ...requestBase(),
        transcript,
        variant: feedbackVariant,
        ...(lesson && { lesson: { level: lesson.level, lesson: lesson.lesson } }),
      })
      .then(({ result }) => setFeedback((result as AiccSandboxFeedbackResult) ?? null))
      .catch(() => undefined)
      .finally(() => setIsSummarising(false));
  };

  const identify = () => {
    if (!languageId) {
      return;
    }

    setIsIdentifying(true);
    sandboxAPI
      .runAiccSandboxAction(languageId, AICC_SANDBOX_ACTION.foreignWordIdentification, { ...requestBase(), transcript })
      .then(({ result }) => setWords((result as AiccSandboxWordsResult) ?? null))
      .catch(() => undefined)
      .finally(() => setIsIdentifying(false));
  };

  return {
    feedback,
    isSummarising,
    summarise,
    words,
    isIdentifying,
    identify,
    clearSupport: () => {
      setFeedback(undefined);
      setWords(undefined);
    },
  };
}
